import { Icon, MenuItem } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { ConflictError } from "apis/errors";
import { ActionPermissionsObject, IThemeStorageModel, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { pluralize } from "js/core/utilities/utilities";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { app } from "js/namespaces";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { SearchBarContainer, UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { ThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { NoMyResourcesThemeNotice } from "js/react/components/Notice";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.TEAMS);

const Container = styled.div`
    background: white;
    padding: 20px;
`;

function TeamSharedThemesPane(props: WorkspaceControllerState) {
    const { resources } = props;
    const [sharedThemes, setSharedThemes] = useState<IThemeStorageModel[]>([]);
    const [fetching, setFetching] = useState(true);

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    useEffect(() => {
        (async () => {
            const sharedThemes = await WorkspaceController.getSharedThemes();
            setSharedThemes(sharedThemes);
            setFetching(false);
        })();
    }, [getObjectHash(resources.filter(resource => resource.resourceType === WorkspaceResourceType.THEME))]);

    const teamThemes: IThemeStorageModel[] = fetching ? [] : sharedThemes.filter(Boolean);

    const handleCreate = () => {
        AppController.navigateToThemeEditor("new-team-theme");
    };

    const handleEdit = async (theme: IThemeStorageModel) => {
        const teamThemeUsageCount = resources.filter(resource =>
            (resource.resourceType === WorkspaceResourceType.PRESENTATION || resource.resourceType === WorkspaceResourceType.PRESENTATION_TEMPLATE) &&
            resource.metadata?.sharedThemeId === theme.id
        ).length;
        // Keep dialog language consistent with existing UX by never showing "0 presentations".
        const impactedPresentationCount = Math.max(teamThemeUsageCount, 1);

        const shouldContinue = await ShowConfirmationDialog({
            title: "Editing team theme",
            message: `Any changes you make will impact ${pluralize(
                "presentation",
                impactedPresentationCount,
                "presentations"
            )} in your workspace.`,
            cancelButtonLabel: "Cancel",
            okButtonLabel: "Continue editing",
            optOutProperty: "editTeamThemeDialog"
        });

        if (!shouldContinue) return;

        AppController.navigateToThemeEditor(theme.id);
    };

    const handleRename = (theme: IThemeStorageModel) => {
        ShowDialog(InputConfirmationDialog, {
            title: t("Rename Theme"),
            message: null,
            input: {
                value: theme.get("name")
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: t("Renaming theme..."),
                        action: async () => {
                            theme.update({ name: name.trim() });
                            await theme.updatePromise;
                        }
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: t("Unable to rename theme"),
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: t("Duplicating theme..."),
                action: async () => {
                    const name = t("Copy of {{name}}", { name: theme.get("name") });
                    await app.themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: t("Unable to duplicate theme"),
                message: err.message
            });
        }
    };

    const handleDelete = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: t("Deleting theme..."),
                action: () => app.themeManager.deleteTeamTheme(theme.id)
            });
        } catch (err) {
            if (err instanceof ConflictError) {
                const title = t("Sorry you can't delete a Team Theme that is currently being used by presentations.");
                const message = err.message;

                if (theme.get("hidden")) {
                    ShowMessageDialog({
                        title,
                        message
                    });
                    return;
                }

                const willHide = await ShowConfirmationDialog({
                    title,
                    message: `${message} ${t("If you want to prevent this Team Theme from being used in additional presentations, you can hide it.")}`,
                    okButtonLabel: t("Hide"),
                });
                if (willHide) {
                    handleToggleHide(theme);
                }

                return;
            }

            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: t("Unable to delete theme"),
                message: err.message
            });
        }
    };

    const handleToggleHide = async (theme: IThemeStorageModel) => {
        try {
            await withProgressDialog({
                title: theme.get("hidden") ? t("Unhiding theme...") : t("Hiding theme..."),
                action: async () => {
                    theme.update({ hidden: !theme.get("hidden") });
                    await theme.updatePromise;
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: t("Unable to toggle hide theme"),
                message: err.message
            });
        }
    };

    return (
        <UIPane>
            <SearchBarContainer className="search-container">
                {canManageSharedResources && (
                    <>
                        <Breadcrumb>{t("Team Themes")}</Breadcrumb>
                        <RoundIconButton onClick={handleCreate}><Icon>add</Icon></RoundIconButton>
                    </>
                )}
                {canUpgradeToManageSharedResources && (
                    <FlexBox center fillWidth padding="40px 0px 0px 0px">
                        <Banner
                            headerText={t("Unlock Team Themes")}
                            bodyText={t("Upgrade your plan to keep colors, fonts, and logos consistent across presentations.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "team_themes" }
                                })
                            }
                        />
                    </FlexBox>
                )}
            </SearchBarContainer>
            <UIPaneResultsContainer style={(canUpgradeToManageSharedResources) ? { paddingTop: "20px" } : {}}>
                <Container>
                    <FetchingClickShield visible={fetching} backgroundColor="#EEEEEE" />
                    {teamThemes.length > 0 && (
                        <ThumbnailGrid
                            items={teamThemes}
                            columns={4}
                            selectedItem={false}
                            renderThumbnail={(child: { item: IThemeStorageModel }) => {
                                const isDefault = child.item.id === sharedThemes[0]?.id;
                                return (
                                    <ThemeThumbnail
                                        item={child.item}
                                        isDefault={isDefault && teamThemes.length > 1}
                                        isDowngraded={canUpgradeToManageSharedResources}
                                        menu={
                                            <IconMenu>
                                                {canUpgradeToManageSharedResources ? [
                                                    <MenuItem key="edit" disabled divider><UpgradeIcon/><Gap5/>{t("Edit Theme...")}</MenuItem>,
                                                    <MenuItem key="rename" disabled divider><UpgradeIcon/><Gap5/>{t("Rename")}</MenuItem>,
                                                    <MenuItem key="duplicate" disabled divider><UpgradeIcon/><Gap5/>{t("Duplicate")}</MenuItem>,
                                                    <hr key="divider" />,
                                                    child.item.get("hidden")
                                                        ? <MenuItem key="unhide" disabled divider><UpgradeIcon/><Gap5/>{t("Show")}</MenuItem>
                                                        : <MenuItem key="hide" disabled divider><UpgradeIcon/><Gap5/>{t("Hide")}</MenuItem>,
                                                ].filter(Boolean) : [
                                                    <MenuItem key="edit" onClick={() => handleEdit(child.item)}>
                                                        <Icon>edit</Icon>{t("Edit Theme...")}
                                                    </MenuItem>,
                                                    <MenuItem key="rename" onClick={() => handleRename(child.item)}>
                                                        <Icon>label</Icon>{t("Rename")}
                                                    </MenuItem>,
                                                    <MenuItem key="duplicate" onClick={() => handleDuplicate(child.item)}>
                                                        <Icon>content_copy</Icon>{t("Duplicate")}
                                                    </MenuItem>,
                                                    <hr key="divider" />,
                                                    child.item.get("hidden") ? (
                                                        <MenuItem key="unhide" onClick={() => handleToggleHide(child.item)}>
                                                            <Icon>visibility_on</Icon>{t("Show")}
                                                        </MenuItem>
                                                    ) : (
                                                        <MenuItem key="hide" onClick={() => handleToggleHide(child.item)}>
                                                            <Icon>visibility_off</Icon>{t("Hide")}
                                                        </MenuItem>
                                                    )
                                                ].filter(Boolean)}
                                                {!isDefault && (
                                                    <MenuItem key="delete" onClick={() => handleDelete(child.item)}>
                                                        <Icon>delete</Icon>{t("Delete...")}
                                                    </MenuItem>
                                                )}
                                            </IconMenu>
                                        }
                                        onDoubleClick={canUpgradeToManageSharedResources ? undefined : (() => handleEdit(child.item))}
                                    />
                                );
                            }}
                        />
                    )}
                </Container>
                {teamThemes.length === 0 && <NoMyResourcesThemeNotice />}
            </UIPaneResultsContainer>
        </UIPane>
    );
}

export default WorkspaceController.withInitializedState(TeamSharedThemesPane);
